import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ListItem, OtpInputComponent } from '@material-library';
import { AuthService } from 'src/app/auth/services/auth.service';
import { ApiService } from 'src/app/core/api.service';
import { LoggerService } from 'src/app/core/logger.service';
import { UserService } from 'src/app/core/user.service';
import { ErrCode, User } from 'src/app/shared/models';
import { TwoFactorPubkeyCreateApiOutput } from 'src/app/shared/models/api/twofactorpubkeycreate-api.model';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';

@Component({
  selector: 'app-dialog-twofactor',
  templateUrl: './dialog-twofactor.component.html',
  styleUrls: ['./dialog-twofactor.component.scss']
})
export class DialogTwofactorComponent implements OnInit {

  @Input() user: User;
  @Input() showTwoFactorDialog: boolean;
  @Output() showTwoFactorDialogChange = new EventEmitter<boolean>();

  public twoFactorTypes: ListItem[] = [];
  public errcode: ErrCode;
  public twofaType;
  public TWOFATYPE_NONE = '0';
  public TWOFATYPE_EMAIL = '1';
  public TWOFATYPE_GOOGLE = '2';
  public showTwofaEmailPrompt: boolean = false;
  public isEnableButtonLoading: boolean = false;
  public backButtonDisabled: boolean = false;
  public showBackupCodeDialog: boolean; //display backup code confirm dialog
  public backupCodesString: string; //this is displayed in-browser for user to copy/paste
  public backupCodes: string[];
  public password: string; //plain password entered by user
  private backupCodeFile: string;
  public safeBackupCodeLink: SafeUrl;
  public showQRCode: boolean = false;
  public twofakey: string;
  public qrdata: string;
  public showVerificationPrompt: boolean = false;
  public verificationCode: string = '';
  public otpDisabled: boolean = false;
  public incorrectOTPError: string = '';
  public showErrCode: boolean = false;
  public newBackupCodes: boolean = false;
  public incorrectPassError: string = '';
  @ViewChild('otpInputComponent') otpInputComponent!: OtpInputComponent;

  constructor(
    private apiService: ApiService,
    private userService: UserService,
    private sanitizer: DomSanitizer,
    private auth: AuthService,
    private log: LoggerService,
    private snackbarService: SnackbarService
  ) { }


  ngOnInit(): void {
    this.showBackupCodeDialog = false;
    this.backupCodesString = '';
    this.twofaType = String(this.user.twofa_type);

    this.twoFactorTypes = [{
      primaryText: 'Enable 2FA via email',
      secondaryText: 'You will be prompted for a 2FA code during web login, which will be sent to your Sync account’s email address.',
      isInteractive: true,
      trail2AdornmentConfig: {
        enabled: true,
        type: 'iconButton',
        size: 'small',
        iconButtonConfig: {
          iconName: 'chevron-right',
          size: 'small',
        },
      },
      onClick: () => this.showTwofaViaEmail()
    },
    {
      primaryText: 'Enable 2FA via Authenticator App',
      secondaryText: 'You will be prompted for a 2FA code during web login, which will be provided by your chosen authenticator app.',
      isInteractive: true,
      trail2AdornmentConfig: {
        enabled: true,
        type: 'iconButton',
        size: 'small',
        iconButtonConfig: {
          iconName: 'chevron-right',
          size: 'small',
        },
      },
      onClick: () => this.showGoogleAuth(),
    }]
  }

  onBack = () => {
    this.errcode = undefined;
    if (this.showTwofaEmailPrompt || this.showQRCode) {
      this.showTwofaEmailPrompt = false;
      this.showQRCode = false;
      this.TWOFATYPE_NONE = '0';
    } else if (this.showVerificationPrompt) {
      this.showQRCode = true;
      this.showVerificationPrompt = false;
    } else if (this.newBackupCodes && !this.backupCodes) {
      this.newBackupCodes = false;
      this.incorrectPassError = '';
    } else {
      this.closeTwoFactorDialog();
    }
  }

  showTwofaViaEmail = () => {
    this.showTwofaEmailPrompt = true;
    this.TWOFATYPE_NONE = '1';
    this.errcode = undefined;
  }

  showGoogleAuth = async () => {
    try {
      const data = await this.apiService.execute<TwoFactorPubkeyCreateApiOutput>('twofactorpubkeycreate', {});
      this.showQRCode = true;
      this.TWOFATYPE_NONE = '1';
      this.twofakey = data.twofakey;
      this.qrdata = 'otpauth://totp/Sync' + '%20' + this.user.email + '?secret=' + data.twofakey + '&issuer=Sync';
    } catch (e) {
      this.errcode = ErrCode.fromException(e);
    }
  }

  disable2FA = async () => {
    try {
      await this.apiService.execute('twofactorupdate', {
        twofaType: this.twofaType,
        disable: 1
      });
      this.userService.refresh();
      this.closeTwoFactorDialog();
      this.snackbarService.showMessage("success", "2FA successfully disabled.");
    } catch (ex) {
      this.errcode = ErrCode.fromException(ex);
      if (!this.errcode.msg || this.errcode.msg === 'An unknown error occurred') {
        this.closeTwoFactorDialog();
        this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
      }
    }
  }

  public update2FA = async () => {
    this.isEnableButtonLoading = true;
    this.backButtonDisabled = true;
    try {
      const response = await this.apiService.execute('twofactorupdate', {
        twofaType: this.twofaType,
        disable: 0
      });
      this.twofaType = String(response['twofaType']);
      this.showTwofaEmailPrompt = false;
      this.TWOFATYPE_NONE = '0';
      this.generateBackupCodes();
    } catch (ex) {
      this.errcode = ErrCode.fromException(ex);
      if (!this.errcode.msg || this.errcode.msg === 'An unknown error occurred') {
        this.errcode = new ErrCode(
          1200,
          'Couldn’t enable two-factor authentication. Try again.'
        );
      }
    } finally {
      this.isEnableButtonLoading = false;
      this.backButtonDisabled = false;
    }
  }

  verifyCode = () => {
    this.showVerificationPrompt = true;
    this.showQRCode = false;
    this.errcode = undefined;
    this.incorrectOTPError = '';
  }

  getOtpValue = (otpValue: string) => {
    this.verificationCode = otpValue;
    this.incorrectOTPError = '';
  }

  public updateGoogleAuth = async () => {
    this.isEnableButtonLoading = true;
    this.otpDisabled = true;
    this.backButtonDisabled = true;
    this.errcode = undefined;
    try {
      const response = await this.apiService.execute('twofactorgoogleauth', {
        twofa_token: this.twofakey,
        verification_code: this.verificationCode
      });
      this.showQRCode = false;
      this.twofaType = String(response['twofaType']);
      this.showVerificationPrompt = false;
      this.backButtonDisabled = false;
      this.generateBackupCodes();
    } catch (err) {
      this.log.error(err);
      this.handleErrorResponse(err);
    } finally {
      this.isEnableButtonLoading = false;
      this.otpDisabled = false;
    }
  }

  /**
   * Handles error response from /twofactorgoogleauth API call
   */
  handleErrorResponse(err) {
    this.errcode = ErrCode.fromException(err);
    this.incorrectOTPError = 'Incorrect verification code';
    if (this.errcode.code === 8024) {
      this.errcode.msg = 'The verification code entered is incorrect. Ensure the code has not expired and try again.';
    }
    if (!this.errcode.msg || this.errcode.msg === 'An unknown error occurred') {
      this.errcode = new ErrCode(
        1200,
        'Couldn’t enable two-factor authentication. Try again.'
      );
      this.clearVerificationInputs();
    }
  }

  public clearVerificationInputs = () => {
    this.otpInputComponent.clearOtpValues();
    this.verificationCode = '';
    this.incorrectOTPError = '';
  }

  public generateNewBackupCodes = () => {
    this.newBackupCodes = true;
    this.backupCodes = undefined;
    this.password = '';
  }

  getPassword = (password: string) => {
    this.password = password;
    this.incorrectPassError = '';
  }

  /**
   * call api for generating 2FA backup codes and set in-browser backup code
   */
  public generateBackupCodes = async () => {
    this.errcode = undefined;
    try {
      this.log.info('calling api to get backup codes');
      
      //password must be hashed before sending to api call. User cannot enter empty password to bypass this
      const hashedPass = (this.password) ? await this.auth.getHashedPassword(this.user.email, this.password) : '';
      const response = await this.apiService.execute('generateuserbackupcode', { password: hashedPass });

      //show download dialog for user to get the codes
      this.backupCodes = response['codes'];
      this.showBackupCodeDialog = true;
      this.backupCodes.forEach((code, idx) => {
        this.backupCodesString += code;
        if (idx != this.backupCodes.length - 1) {
          this.backupCodesString += '\n';
        }
      });
      this.userService.refresh();

    } catch (ex) {
      this.log.error(ex);
      this.errcode = ErrCode.fromException(ex);

      if (this.errcode.code == 8101) {
        this.showErrCode = true;
        this.incorrectPassError = 'Enter a valid password';
        this.errcode.msg = 'Incorrect password';
      }
      if (!this.errcode.msg || this.errcode.msg === 'An unknown error occurred') {
        this.closeTwoFactorDialog();
        this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
      }
    }
  }

  /**
   * create a downloadable file containing backup codes
   */
  downloadBackupCodes = () => {
    this.log.info('Creating download file');
    let text = 'Sync.com\'s back-up codes for 2-step verification:\n';
    const fileName = 'backup_codes';

    text += this.backupCodesString;

    const data = new Blob([text], {
      type: 'text/plain'
    });

    this.backupCodeFile = window.URL.createObjectURL(data);
    this.safeBackupCodeLink = this.sanitizer.bypassSecurityTrustResourceUrl(this.backupCodeFile);

    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      //save file for IE
      (window.navigator as any).msSaveOrOpenBlob(data, fileName);
    } else {
      //save for other browsers: Chrome, Firefox
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = this.backupCodeFile;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }

    URL.revokeObjectURL(this.backupCodeFile);
  }

  /**
    * copy backup codes to clipboard
    *
    */
  copyCodesToClipboard = async () => {
    {
      try {
        await navigator.clipboard.writeText(this.backupCodesString);
        this.snackbarService.showMessage("success", "Backup codes successfully copied.");
      } catch (err) {
        console.error('Unable to copy to clipboard', err);
        this.snackbarService.showMessage("critical", "Couldn’t complete action. Try again.");
      }
    }
  }

  closeTwoFactorDialog = () => {
    this.showTwoFactorDialog = false;
    setTimeout(() => {
      this.showTwoFactorDialogChange.next(false);
    });
  }

}
